/**
 * BarCache — two-tier persistent cache for OHLCV bars.
 *
 * Tier 1: in-memory `Map` (LRU) for instant repeat hits within a session.
 *         Capped to MEMORY_LIMIT entries.
 * Tier 2: IndexedDB (persistent) keyed by `${symbol}|${date}|${timeframe}`.
 *         Survives reloads. FIFO-evicted past PERSISTENT_LIMIT entries.
 *
 * Why IndexedDB instead of `{dataDir}/bars-cache/`:
 *   - Bars are read-heavy; IDB is fast for keyed lookups
 *   - Browser handles eviction/quota management
 *   - No file-system permission concerns
 *   - User-visible bar files (the SOURCE) still live on disk in {dataDir}/bars/
 *     — this cache is for the parsed/normalized result
 *
 * Cache entry shape:
 *   { bars: Bar[], source: string, fetchedAt: number, accessedAt: number }
 *
 * NOTE: historical bars are immutable — once fetched, the bars for
 * MNQ@2026-04-15@5m won't change. So no TTL needed for the data itself;
 * eviction is purely about disk-space management.
 */

const DB_NAME = 'journifi-bars';
const STORE_NAME = 'bars';
const DB_VERSION = 1;

const MEMORY_LIMIT = 200;       // keep last ~200 sessions in RAM (cheap)
const PERSISTENT_LIMIT = 5000;  // ~5000 sessions on disk (huge headroom)
const PERSISTENT_TARGET = 4000; // evict down to this when over LIMIT

export class BarCache {
  constructor() {
    this._memory = new Map(); // key → entry, in insertion order (LRU via re-set on access)
    this._dbPromise = null;
    this._evictPromise = null;
  }

  // ── IndexedDB plumbing ────────────────────────────────────────────────
  _openDb() {
    if (typeof indexedDB === 'undefined') return Promise.resolve(null);
    if (this._dbPromise) return this._dbPromise;
    this._dbPromise = new Promise((resolve) => {
      try {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
            store.createIndex('accessedAt', 'accessedAt');
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => {
          console.warn('[BarCache] IndexedDB open failed:', req.error);
          resolve(null);
        };
      } catch (e) {
        console.warn('[BarCache] IndexedDB unavailable:', e);
        resolve(null);
      }
    });
    return this._dbPromise;
  }

  _tx(mode) {
    return this._openDb().then(db => db ? db.transaction(STORE_NAME, mode).objectStore(STORE_NAME) : null);
  }

  // ── Public API ────────────────────────────────────────────────────────

  async get(key) {
    // Memory tier
    if (this._memory.has(key)) {
      const entry = this._memory.get(key);
      // Re-insert to bump LRU position
      this._memory.delete(key);
      this._memory.set(key, entry);
      return entry;
    }

    // Persistent tier
    const store = await this._tx('readwrite');
    if (!store) return null;
    return new Promise(resolve => {
      const req = store.get(key);
      req.onsuccess = () => {
        const row = req.result;
        if (!row) { resolve(null); return; }
        // Bump accessedAt for LRU eviction ordering
        row.accessedAt = Date.now();
        try { store.put(row); } catch { /* tx may be closing */ }
        // Promote to memory tier
        this._setMemory(key, row);
        resolve(row);
      };
      req.onerror = () => resolve(null);
    });
  }

  async set(key, value) {
    const entry = {
      key,
      bars: value.bars,
      source: value.source || 'unknown',
      fetchedAt: value.fetchedAt || Date.now(),
      accessedAt: Date.now(),
    };
    this._setMemory(key, entry);

    const store = await this._tx('readwrite');
    if (!store) return;
    try {
      store.put(entry);
    } catch (e) {
      console.warn('[BarCache] put failed:', e);
    }
    // Eviction is async + debounced — don't block writes
    this._scheduleEvict();
  }

  async delete(key) {
    this._memory.delete(key);
    const store = await this._tx('readwrite');
    if (!store) return;
    try { store.delete(key); } catch { /* ignore */ }
  }

  async clear() {
    this._memory.clear();
    const store = await this._tx('readwrite');
    if (!store) return;
    try { store.clear(); } catch { /* ignore */ }
  }

  async stats() {
    const store = await this._tx('readonly');
    if (!store) return { entries: 0, memory: this._memory.size, persistent: 0 };
    return new Promise(resolve => {
      const req = store.count();
      req.onsuccess = () => resolve({
        entries: req.result,
        memory: this._memory.size,
        persistent: req.result,
      });
      req.onerror = () => resolve({ entries: 0, memory: this._memory.size, persistent: 0 });
    });
  }

  // ── Internal: LRU + eviction ──────────────────────────────────────────

  _setMemory(key, entry) {
    if (this._memory.has(key)) this._memory.delete(key);
    this._memory.set(key, entry);
    while (this._memory.size > MEMORY_LIMIT) {
      const oldest = this._memory.keys().next().value;
      this._memory.delete(oldest);
    }
  }

  _scheduleEvict() {
    if (this._evictPromise) return;
    this._evictPromise = new Promise(resolve => {
      setTimeout(async () => {
        try { await this._evict(); } catch { /* ignore */ }
        this._evictPromise = null;
        resolve();
      }, 2000); // debounce — multiple writes coalesce
    });
  }

  async _evict() {
    const store = await this._tx('readwrite');
    if (!store) return;
    return new Promise(resolve => {
      const countReq = store.count();
      countReq.onsuccess = () => {
        if (countReq.result <= PERSISTENT_LIMIT) { resolve(); return; }
        const toRemove = countReq.result - PERSISTENT_TARGET;
        // Walk the accessedAt index ascending, delete the oldest `toRemove`
        const idx = store.index('accessedAt');
        const cursorReq = idx.openCursor();
        let removed = 0;
        cursorReq.onsuccess = (e) => {
          const cursor = e.target.result;
          if (!cursor || removed >= toRemove) {
            console.log(`[BarCache] Evicted ${removed} oldest bar entries (cap ${PERSISTENT_LIMIT}, target ${PERSISTENT_TARGET})`);
            resolve();
            return;
          }
          cursor.delete();
          removed++;
          cursor.continue();
        };
        cursorReq.onerror = () => resolve();
      };
      countReq.onerror = () => resolve();
    });
  }
}
